import React from 'react';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Card, CardBody, Spinner } from '@heroui/react';
import { GuideStepType, StepTranslation } from '@/services/apis/guides';
import { GuideStep } from '@/services/apis/guides';
import { RichTextStep, VideoStep, PdfStep, ImageStep, CodeStep, QuizStep } from './steps';

import { GuideProgress } from '@/types';

interface GuideViewerStepCardProps {
  step: GuideStep;
  stepIndex: number;
  totalSteps: number;
  isCompleted: boolean;
  onComplete: () => void;
  onNext: () => void;
  onPrevious: () => void;
  getStepTranslation: (step: GuideStep) => any;
  guideId: string;
  progress?: GuideProgress | null;
  isSavingComplete?: boolean;
}

const renderStepContent = (
  step: GuideStep,
  stepTranslation: StepTranslation,
  guideId: string,
  onComplete?: () => void,
  progress?: GuideProgress | null,
) => {
  if (!stepTranslation) {
    return <div className="text-center text-gray-500 py-8">No translation available</div>;
  }

  const content = stepTranslation.content || {};
  const foundGlossaries = step.glossaries || [];
  const stepTitle = stepTranslation.title || 'Untitled Step';

  switch (step.type) {
    case GuideStepType.RICH_TEXT:
      return <RichTextStep content={content} title={stepTitle} glossaries={foundGlossaries} />;
    case GuideStepType.VIDEO:
      return <VideoStep content={content} title={stepTitle} />;
    case GuideStepType.PDF:
      return <PdfStep content={content} title={stepTitle} />;
    case GuideStepType.IMAGE:
      return <ImageStep content={content} title={stepTitle} />;
    case GuideStepType.CODE:
      return <CodeStep content={content} title={stepTitle} />;
    case GuideStepType.QUESTION:
      return (
        <QuizStep
          content={content}
          title={stepTitle}
          stepId={step.id}
          guideId={guideId}
          questionConfig={step.questionConfig}
          onComplete={onComplete}
          savedScore={progress?.quizScores?.[step.id]}
        />
      );
    default:
      return <div>Unknown step type</div>;
  }
};

const GuideViewerStepCard: React.FC<GuideViewerStepCardProps> = ({
  step,
  stepIndex,
  totalSteps,
  isCompleted,
  onComplete,
  onNext,
  onPrevious,
  getStepTranslation,
  guideId,
  progress,
  isSavingComplete = false,
}) => {
  const stepTranslation = getStepTranslation(step);

  return (
    <Card className="min-w-[100%] overflow-visible">
      <CardBody className="p-8 md:p-12 overflow-visible">
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-foreground-500" />
            <span className="text-sm text-foreground-500">
              Step {stepIndex + 1} of {totalSteps}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {stepTranslation?.title || 'Untitled Step'}
          </h2>
        </div>
        {/* Step content */}
        <div className="flex-1 mb-8">{renderStepContent(step, stepTranslation, guideId, onComplete, progress)}</div>
        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t border-divider mt-6">
          {step.type !== GuideStepType.QUESTION && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-3 cursor-pointer">
                {isSavingComplete ? (
                  <Spinner size="sm" className="w-5 h-5" />
                ) : (
                  <input
                    type="checkbox"
                    checked={isCompleted}
                    onChange={onComplete}
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    aria-label="Mark step as complete"
                  />
                )}
                <span className="text-sm font-medium text-foreground">Mark as complete</span>
              </label>
            </div>
          )}
          {step.type === GuideStepType.QUESTION && <div />}

          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              variant="flat"
              isDisabled={stepIndex === 0}
              onPress={onPrevious}
              aria-label="Previous step"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              isIconOnly
              variant="flat"
              isDisabled={stepIndex === totalSteps - 1}
              onPress={onNext}
              aria-label="Next step"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default GuideViewerStepCard;
